import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { reorderListItem } from '../../../lib/reorder-list';
import { DragHandleIcon } from '../../icons/DragHandleIcon';

interface ReorderableCvRenderItem<T> {
  item: T;
  index: number;
  isActive: boolean;
}

interface ReorderableCvListProps<T extends object> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (params: ReorderableCvRenderItem<T>) => React.ReactNode;
  getItemLabel: (item: T, index: number) => string;
  keyExtractor?: (item: T, index: number) => string | undefined;
  emptyState?: React.ReactElement | null;
  footer?: React.ReactElement | null;
}

/** Web drag-and-drop list with arrow-key ordering on the focused handle. */
export function ReorderableCvList<T extends object>({
  items,
  onReorder,
  renderItem,
  getItemLabel,
  keyExtractor,
  emptyState,
  footer,
}: ReorderableCvListProps<T>) {
  const { t } = useTranslation('seeker');
  const generatedKeys = useRef(new WeakMap<object, string>());
  const nextKey = useRef(0);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const resolveKey = (item: T, index: number) => {
    const explicit = keyExtractor?.(item, index)?.trim();
    if (explicit) return explicit;
    const known = generatedKeys.current.get(item);
    if (known) return known;
    const generated = `cv-list-item-${nextKey.current++}`;
    generatedKeys.current.set(item, generated);
    return generated;
  };

  const move = (fromIndex: number, toIndex: number) => {
    const reordered = reorderListItem(items, fromIndex, toIndex);
    if (reordered !== items) onReorder(reordered);
  };

  return (
    <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {items.map((item, index) => {
        const label = getItemLabel(item, index);
        const isDragging = draggedIndex === index;
        const isDropTarget = overIndex === index && !isDragging;
        return (
          <div
            key={resolveKey(item, index)}
            role="listitem"
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (draggedIndex !== null && draggedIndex !== index) {
                setOverIndex(index);
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              const source =
                draggedIndex ?? Number(event.dataTransfer.getData('text/plain'));
              if (Number.isInteger(source)) move(source, index);
              setDraggedIndex(null);
              setOverIndex(null);
            }}
            style={{
              display: 'flex',
              alignItems: 'stretch',
              gap: 8,
              opacity: isDragging ? 0.55 : 1,
              borderRadius: 16,
              boxShadow: isDropTarget
                ? '0 0 0 2px var(--tabbio-focus-ring)'
                : 'none',
              transition: 'opacity 150ms, box-shadow 150ms, transform 150ms',
            }}
          >
            <button
              type="button"
              draggable
              aria-label={t('cv.edit.reorderItem', { item: label })}
              title={t('cv.edit.dragToReorder')}
              onDragStart={(event) => {
                setDraggedIndex(index);
                setOverIndex(null);
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', String(index));
              }}
              onDragEnd={() => {
                setDraggedIndex(null);
                setOverIndex(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  move(index, index - 1);
                }
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  move(index, index + 1);
                }
              }}
              style={{
                width: 36,
                minHeight: 56,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--input-border)',
                borderRadius: 12,
                backgroundColor: 'var(--hover)',
                color: 'var(--text-tertiary)',
                cursor: isDragging ? 'grabbing' : 'grab',
                fontFamily: 'inherit',
              }}
            >
              <DragHandleIcon size={20} color="currentColor" />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              {renderItem({ item, index, isActive: isDragging })}
            </div>
          </div>
        );
      })}
      {items.length === 0 ? emptyState : null}
      {footer}
    </div>
  );
}
